import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';

  const filteredComics = useMemo(() => {
    if (activeSlug === 'all') return comics;
    const categoryMap: Record<string, string> = {
      korean: '韩漫',
      japanese: '日漫',
      chinese: '国漫',
    };
    if (categoryMap[activeSlug]) {
      return comics.filter((c) => c.categorySlug === activeSlug);
    }
    return comics.filter((c) => c.tags.includes(categories.find((cat) => cat.slug === activeSlug)?.name || ''));
  }, [activeSlug]);

  const activeCategoryName =
    categories.find((c) => c.slug === activeSlug)?.name || '全部';

  const categorySEO = {
    title: `女神漫画下拉式${activeCategoryName} | 女神漫画网分类阅读`,
    description: `女神漫画网提供丰富的女神漫画下拉式${activeCategoryName}免费全集，流畅滚动沉浸式阅读体验，热门${activeCategoryName}作品每日更新。`,
    keywords: `女神漫画下拉式${activeCategoryName},女神漫画下拉式在线阅读,女神漫画下拉式免费,女神漫画下拉式全集,下拉式漫画大全`,
    canonicalUrl: `https://nvshenmh.com/category/${activeSlug}`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `女神漫画 - ${activeCategoryName}分类`,
      description: `女神漫画网${activeCategoryName}分类页面，提供下拉式在线阅读`,
      url: `https://nvshenmh.com/category/${activeSlug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '女神漫画网',
        url: 'https://nvshenmh.com/',
      },
    },
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://nvshenmh.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: activeCategoryName,
        item: `https://nvshenmh.com/category/${activeSlug}`,
      },
    ],
  };

  return (
    <>
      <SEO data={categorySEO} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />

      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium">
              {activeCategoryName}
            </span>
          </nav>
        </div>

        {/* Category Tabs */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 mb-6">
          <div className="flex flex-wrap items-center gap-2 p-1 rounded-xl bg-background-100/70">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Comic Grid */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-16">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-foreground-950">
              女神漫画下拉式{activeCategoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredComics.length} 部作品 ·
              女神漫画下拉式免费在线阅读
            </p>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">
                该分类下暂无漫画，去看看其他分类吧
              </p>
              <Link
                to="/category/all"
                className="inline-block mt-4 px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                查看全部
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}